import { FormEvent, useEffect, useState } from "react";
import { RdsRegistration, api } from "../api";
import { useAuth } from "../auth";

export function DatabasesPage() {
  const { token } = useAuth();
  const [rds, setRds] = useState<RdsRegistration[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [formBusy, setFormBusy] = useState(false);
  const [formNotice, setFormNotice] = useState<{ tone: "ok" | "err"; text: string } | null>(null);

  const [displayName, setDisplayName] = useState("");
  const [host, setHost] = useState("");
  const [port, setPort] = useState("3306");
  const [secretArn, setSecretArn] = useState("");

  const reload = () => {
    if (!token) return;
    api
      .listRds(token)
      .then(setRds)
      .catch((e) => setError(String(e?.message ?? e)));
  };

  useEffect(reload, [token]);

  const onRefreshSchemas = async (id: number) => {
    if (!token) return;
    setBusyId(id);
    try {
      await api.refreshRdsSchemas(token, id);
      reload();
    } catch (e) {
      alert(`Refresh failed: ${(e as { message?: string })?.message ?? e}`);
    } finally {
      setBusyId(null);
    }
  };

  const onDelete = async (r: RdsRegistration) => {
    if (!token) return;
    if (
      !window.confirm(
        `Delete RDS registration #${r.id} (${r.display_name})?\n\nThis cascades to delete all schema_grants pointing at it.`,
      )
    )
      return;
    setBusyId(r.id);
    try {
      await api.deleteRds(token, r.id);
      reload();
    } catch (e) {
      alert(`Delete failed: ${(e as { message?: string })?.message ?? e}`);
    } finally {
      setBusyId(null);
    }
  };

  const resetForm = () => {
    setDisplayName("");
    setHost("");
    setPort("3306");
    setSecretArn("");
    setFormNotice(null);
  };

  const onRegister = async (e: FormEvent) => {
    e.preventDefault();
    if (!token) return;
    setFormNotice(null);
    const trimmedName = displayName.trim();
    const trimmedHost = host.trim();
    const trimmedArn = secretArn.trim();
    const p = Number(port);
    if (!trimmedName || !trimmedHost || !trimmedArn) {
      setFormNotice({ tone: "err", text: "display name, host, and secret ARN are required." });
      return;
    }
    if (!Number.isFinite(p) || p < 1 || p > 65535) {
      setFormNotice({ tone: "err", text: "Port must be a number between 1 and 65535." });
      return;
    }
    setFormBusy(true);
    try {
      const res = await api.createRds(token, {
        display_name: trimmedName,
        host: trimmedHost,
        port: Math.trunc(p),
        secret_arn: trimmedArn,
      });
      reload();
      setFormNotice({
        tone: "ok",
        text: res.created ? `Created registration #${res.id}.` : `Already registered (id ${res.id}); schemas refreshed.`,
      });
      if (res.created) resetForm();
    } catch (err: unknown) {
      const message =
        typeof err === "object" && err && "message" in err ? String((err as { message?: string }).message) : String(err);
      setFormNotice({ tone: "err", text: message });
    } finally {
      setFormBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Database management</h2>
      </div>
      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</div>
      )}

      <div className="card p-4 space-y-3">
        <h3 className="text-sm font-semibold text-slate-900">Register RDS</h3>
        <form className="space-y-3" onSubmit={(e) => void onRegister(e)}>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Display name</label>
              <input className="input" value={displayName} onChange={(e) => setDisplayName(e.target.value)} required />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Host</label>
              <input className="input font-mono text-sm" value={host} onChange={(e) => setHost(e.target.value)} required />
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="sm:col-span-1">
              <label className="block text-xs font-medium text-slate-600 mb-1">Port</label>
              <input className="input font-mono text-sm w-28" inputMode="numeric" value={port} onChange={(e) => setPort(e.target.value)} />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Secrets Manager ARN</label>
            <textarea
              className="input font-mono text-xs min-h-[4.5rem]"
              value={secretArn}
              onChange={(e) => setSecretArn(e.target.value)}
              required
              placeholder="arn:aws:secretsmanager:..."
            />
            <p className="text-xs text-slate-500 mt-1">
              Uses the RDS instance IAM role — the secret payload must expose MySQL hostname, username, and password consistent with rd-mcp.
            </p>
          </div>
          {formNotice && (
            <div
              className={`text-sm rounded px-3 py-2 border ${
                formNotice.tone === "ok"
                  ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                  : "bg-red-50 text-red-800 border-red-200"
              }`}
            >
              {formNotice.text}
            </div>
          )}
          <div className="flex gap-2">
            <button type="submit" className="btn-primary" disabled={formBusy}>
              {formBusy ? "Registering…" : "Register / refresh schemas"}
            </button>
            <button type="button" className="btn" disabled={formBusy} onClick={resetForm}>
              Reset fields
            </button>
          </div>
        </form>
      </div>

      <div className="card overflow-hidden">
        {rds === null ? (
          <p className="p-4 text-sm text-slate-500">Loading…</p>
        ) : rds.length === 0 ? (
          <p className="p-4 text-sm text-slate-500">No databases registered yet.</p>
        ) : (
          <table className="table-clean">
            <thead>
              <tr>
                <th className="w-12">id</th>
                <th>display_name</th>
                <th>host:port</th>
                <th>secret</th>
                <th>schemas</th>
                <th className="text-right">actions</th>
              </tr>
            </thead>
            <tbody>
              {rds.map((r) => (
                <tr key={r.id} className="border-t border-slate-100">
                  <td className="font-mono">{r.id}</td>
                  <td>{r.display_name}</td>
                  <td className="text-xs text-slate-500">
                    {r.host}:{r.port}
                  </td>
                  <td className="text-xs">
                    <SecretArnCell arn={r.secret_arn} />
                  </td>
                  <td className="text-xs">{r.schemas.join(", ") || "—"}</td>
                  <td className="text-right space-x-2">
                    <button
                      type="button"
                      onClick={() => void onRefreshSchemas(r.id)}
                      disabled={busyId === r.id}
                      className="btn"
                    >
                      refresh schemas
                    </button>
                    <button
                      type="button"
                      onClick={() => void onDelete(r)}
                      disabled={busyId === r.id}
                      className="btn-danger"
                    >
                      delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <p className="text-xs text-slate-500">
        Schema access for MCP users is managed under Account management → <strong>schema grants</strong>. See deploy/mcp-portal/SPEC.md §4.4–4.6.
      </p>
    </div>
  );
}

function SecretArnCell({ arn }: { arn: string }) {
  const [revealed, setRevealed] = useState(false);
  const [copied, setCopied] = useState(false);
  // Show only the friendly name + the AWS-allocated suffix at the end so an
  // operator can recognise it ("acc.apac-rds-cw-uk-datamart-TKBlO4") without
  // pasting the full ARN into screenshots.
  const masked = (() => {
    const parts = arn.split(":");
    const tail = parts[parts.length - 1] ?? arn;
    const friendly = tail.startsWith("secret/") ? tail.slice(7) : tail;
    return `…:secret:${friendly}`;
  })();
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(arn);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1200);
    } catch {
      // ignore - some browsers refuse without https / focus
    }
  };
  return (
    <span className="inline-flex items-center gap-2">
      <code className="font-mono text-[11px] text-slate-700 max-w-[18rem] truncate" title={revealed ? arn : "Click reveal to see full ARN"}>
        {revealed ? arn : masked}
      </code>
      <button type="button" onClick={() => setRevealed((v) => !v)} className="btn text-[10px] py-0.5 px-1.5">
        {revealed ? "hide" : "reveal"}
      </button>
      <button type="button" onClick={() => void onCopy()} className="btn text-[10px] py-0.5 px-1.5" title="Copy ARN to clipboard">
        {copied ? "copied" : "copy"}
      </button>
    </span>
  );
}
